/* eslint max-lines: ["error", {"max": 250, "skipComments": true}] */

import React from 'react';
import classNames from 'classnames';
import { Connect, PropsFromRedux } from './SearchBarFieldContainer';
import { SearchAutocomplete } from '../dropdown/SearchAutocomplete';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { UserContextType } from '../../../../user/user.types';
import { withUser } from '../../../../user/UserContext';
import { getRandomSearchPlaceholder } from './searchPlaceholders';
import { OutsideFocusHandler } from '../../../../components/dropdown/outside-handler/OutsideFocusHandler';

type Props = PropsFromRedux & UserContextType;

type State = {
  autocompleteNav: string;
  isOpen: boolean;
  placeholder: string;
};

export class RawSearchBarField extends React.Component<Props, State> {
  input = React.createRef<HTMLInputElement>();

  beforePasting: string = '';

  pasteFlag: boolean = false;

  meta: { [s: string]: any } = {};

  state: State = {
    autocompleteNav: '',
    isOpen: false,
    placeholder: '',
  };

  componentDidMount() {
    this.initPlaceholder();
  }

  initPlaceholder = () => {
    this.setState({
      placeholder: window.mobile
        ? 'Recherchez par mots-clés'
        : getRandomSearchPlaceholder(),
    });
  };

  handleClickOutside = () => {
    const { onUserType } = this.props;
    const { autocompleteNav } = this.state;
    if (autocompleteNav !== '') {
      onUserType(autocompleteNav);
      this.setState({ autocompleteNav: '' });
    }
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
      this.initPlaceholder();
    }
    if (this.input.current) {
      this.input.current.blur();
    }
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const { query, onUserType, onQueryPaste } = this.props;
    const { autocompleteNav } = this.state;
    if (autocompleteNav !== '') {
      doctrineAnalytics('Search AutocompleteSuggestedQueryCompleted', {
        autocomplete_array: this.meta.items,
        position: this.meta.position,
        selected_query: autocompleteNav,
        previous_query: query,
      });
      this.meta = {};
      this.setState({
        autocompleteNav: '',
      });
    }

    window.scrollTo(0, 0);

    if (this.pasteFlag) {
      this.pasteFlag = false;

      const hasQueryChanged = value !== this.beforePasting;
      if (hasQueryChanged) {
        const isQueryEnhanced = value.includes(this.beforePasting);
        onQueryPaste(value, isQueryEnhanced);
      }

      this.beforePasting = '';
    } else {
      onUserType(value);
    }
  };

  onSearchFocus = () => {
    this.setState({ isOpen: true });
    this.setState({ placeholder: '' });
  };

  onSubmit = () => {
    const { submitCurrentQuery, query } = this.props;
    const { autocompleteNav } = this.state;
    window.scrollTo(0, 0);
    submitCurrentQuery(autocompleteNav || query);
    this.setState({ isOpen: false });
    this.initPlaceholder();
  };

  onClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isOpen: false });
    this.initPlaceholder();
    if (this.input.current) {
      this.input.current.blur();
    }
  };

  handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    // if we paste a new query that replace totally the old one we reset the cats
    this.beforePasting = event.currentTarget.value;
    this.pasteFlag = true;
  };

  onItemSelected = (newQuery: string, source: string) => {
    if (this.input.current) {
      this.input.current.blur();
    }
    const { followAutocomplete } = this.props;
    followAutocomplete(newQuery, source);
    this.setState({
      isOpen: false,
      autocompleteNav: '',
    });
  };

  updateQuery = (newQuery: string, meta: { [s: string]: any }) => {
    this.setState({
      autocompleteNav: meta.type !== 'written_query' ? newQuery : '',
    });
    this.meta = meta;
  };

  manualClose = () => {
    this.setState({ isOpen: false });
  };

  onClear = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    doctrineAnalytics('Search DeleteQueryClicked');
    this.setState({ autocompleteNav: '' });
    this.props.onUserType('');
    if (this.input.current) {
      this.input.current.focus();
    }
  };

  render() {
    const { query, loggedIn } = this.props;
    const { autocompleteNav, isOpen, placeholder } = this.state;
    const hasQuery = query.length > 0;
    return (
      <>
        <OutsideFocusHandler
          className="search-bar"
          onOutsideClick={this.handleClickOutside}
        >
          <label className="search-bar__label">
            {isOpen && (
              <button
                aria-label="Close search bar"
                type="button"
                onMouseDown={this.onClose}
                className="search-bar__close"
              />
            )}
            <form action="" onSubmit={evt => evt.preventDefault()}>
              <input
                ref={this.input}
                data-test-id="search-input"
                name="search-input"
                data-tour="search-advice"
                className={classNames(
                  'search-bar__input',
                  isOpen && 'search-bar__input--focused',
                  hasQuery && 'search-bar__input--actions',
                )}
                id="search_input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={placeholder}
                value={autocompleteNav || query}
                onChange={this.onChange}
                onPaste={this.handlePaste}
                onFocus={this.onSearchFocus}
                maxLength={1000}
              />
            </form>
            <>
              {hasQuery && (
                <button
                  aria-label="Clear search bar"
                  type="button"
                  onClick={this.onClear}
                  className="search-bar__clear"
                />
              )}
              <button
                aria-label="Submit search"
                type="button"
                disabled={query.length === 0}
                onClick={this.onSubmit}
                className="search-bar__button"
              />
            </>
          </label>

          {isOpen && (
            <SearchAutocomplete
              query={query}
              isLoggedIn={loggedIn}
              onItemSelected={this.onItemSelected}
              updateQuery={this.updateQuery}
              manualClose={this.manualClose}
            />
          )}
        </OutsideFocusHandler>
      </>
    );
  }
}

export const SearchBarField = withUser(Connect(RawSearchBarField));
